/* eslint-disable camelcase */
import {
  createSelector,
} from 'reselect';

import {
  getProfile,
} from './profile';

import {
  getResourceMap,
  getResourceMappedList,
} from './resources';

export const invoicesSelector = (state => state.payments.invoices);
export const transactionsSelector = (state => state.payments.transactions);

export const getTransactions = createSelector(
  [
    getResourceMappedList('products'),
    invoicesSelector,
    transactionsSelector,
  ],
  (
    products,
    invoices,
    transactions,
  ) => transactions.map(
    transaction => ({
      ...transaction,
      invoice_data: invoices.find(
        i => i.charge === transaction.id,
      ),
      product_data: products.find(
        product => product.id === Number(transaction.metadata?.product_id),
      ),
    }),
  ),
);

const getBillingReason = (lines) => {
  if (lines) {
    try {
      let line;
      if (typeof lines === 'string') {
        [line] = JSON.parse(lines)
          .sort(
            (a, b) => (a.amount > b.amount ? -1 : 1),
          );
      } else if (lines.data) {
        [line] = lines.data;
      }

      return line?.plan?.name || line.description;
    } catch (e) {
      /* handle error */
      console.log(e);
    }
  }

  return '';
};

export const getInvoices = createSelector(
  [
    getResourceMap('products'),
    getResourceMap('productPlans'),
    invoicesSelector,
  ],
  (
    products,
    productPlans,
    invoices,
  ) => invoices
    .filter(
      (invoice, i) => (i !== invoices.length - 1 || invoice.total > 0),
    )
    .map(
      invoice => {
        const plan = productPlans[invoice?.product_plan_id] || null;
        const product = products[invoice?.product_id] || null;

        const description = invoice.product_name
          || invoice.product_plan_name
          || plan?.name
          || product?.name
          || getBillingReason(invoice.lines)
          || invoice.description;

        return {
          ...invoice,
          plan_data: plan,
          product_data: product,
          normalizedDescription: description,
        };
      },
    ),
);

export const plansSelector = (state => state.payments.plans
  .sort((a, b) => {
    if (
      Number(a.data.metadata.max_clients_count) > Number(b.data.metadata.max_clients_count)
    ) return 1;
    if (
      Number(a.data.metadata.max_clients_count) < Number(b.data.metadata.max_clients_count)
    ) return -1;
    return 0;
  })
  .sort((a, b) => {
    if (
      a.interval === 'year' && b.interval === 'month'
    ) return 1;
    if (
      a.interval === 'month' && b.interval === 'year'
    ) return -1;
    return 0;
  }));

export const currentCardsSelector = (
  ({ payments: { currentCustomer } }) => {
    const defaultSource = currentCustomer.default_source;
    return (currentCustomer.sources
      ? [
        currentCustomer.sources.data.find(e => e.id === defaultSource),
        ...currentCustomer.sources.data.filter(e => e.id !== defaultSource),
      ].filter(Boolean)
      : []);
  }
);

export const defaultCardIdSelector = (
  ({ payments: { currentCustomer } }) => (
    currentCustomer.invoice_settings?.default_payment_method
    || currentCustomer.default_source || '' // eslint-disable-line
  )
);

export const getSubscription = createSelector(
  [
    getProfile('is_pro'),
    state => state.payments.currentCustomer.subscriptions?.data[0],
  ], (
    isPro,
    subscription,
  ) => {
    const freemium = isPro
      ? {}
      : {
        plan: {
          id: 'freemium',
          name: 'Freemium',
        },
      };

    return subscription || freemium;
  },
);

export const getSubscriptions = (
  ({ payments: { currentCustomer } }) => (
    currentCustomer.subscriptions?.data || []
  )
);

export const getLastFailedInvoice = (
  ({ payments: { lastFailedInvoice } }) => lastFailedInvoice
);

export const getSetupIntent = (
  ({ payments: { setupIntent } }) => setupIntent
);

export const getPaymentRequestsState = keyOrState => (
  (typeof keyOrState === 'string')
    ? state => state.payments[keyOrState]
    : [
      'getPlansState',
      'setPlanState',
      'getCustomerState',
      'paymentAttemptState',
      'getTransactionsState',
    ].reduce((acc, key) => ({
      ...acc,
      [key]: keyOrState.payments[key],
    }), {})
);
